/**
 * PR-H — ZOOM Window rubber band.
 *
 * Screen-space HTML rectangle that follows the mouse drag during the
 * `ZOOM Window` command. Drawn entirely outside the GL canvas so the
 * overlay can use native `pointerEvents: "none"` and still receive
 * the cursor's actual on-screen position via React props.
 *
 * # Two-corner contract
 *
 * The overlay accepts `firstCorner` (set on the first click) and
 * `currentCursor` (mouse-move follow). Both are CSS pixels relative
 * to the canvas's parent container. The rectangle is the AABB of
 * those two points; orientation is preserved via a CSS class
 * (`is-window` for L-to-R, `is-crossing` for R-to-L) so the host can
 * style left-to-right vs right-to-left differently if it chooses.
 *
 * The CSS itself lives next to the existing
 * `.drawing-stage-zoom-rubber` class in the SVG stage stylesheet —
 * scene3d shares the same visual contract via the same className.
 *
 * # Hidden when null
 *
 * When `firstCorner` is `null`, the overlay returns `null`. The host
 * mounts/unmounts unconditionally; this prop-driven null is sufficient.
 */

import * as React from "react";

import type { ZoomRubberRectState } from "./overlayStateTypes";

/**
 * Props for the new `ZoomRubberRect` component (PR-K).
 *
 * Replaces the older two-prop `ZoomRubberProps` API with a single
 * `state` slice that mirrors `OverlayState["zoomRubber"]`. The host
 * (`Scene3DDomOverlays`) passes the slice straight through.
 */
export interface ZoomRubberRectProps {
  readonly state: ZoomRubberRectState | null;
}

/**
 * Yellow dashed AABB drawn during a ZOOM W picking gesture. Paints the
 * rectangle between `startScreen` (first click) and `currentScreen`
 * (live cursor). Coordinates in CSS pixels relative to the overlay
 * container; the component derives the AABB itself.
 *
 * Returns `null` when the overlay is hidden so the host can mount us
 * unconditionally (state-driven visibility, AutoCAD-parity colour).
 */
export function ZoomRubberRect(
  props: ZoomRubberRectProps,
): React.ReactElement | null {
  const { state } = props;
  if (!state || !state.visible) return null;

  const left = Math.min(state.startScreen.x, state.currentScreen.x);
  const top = Math.min(state.startScreen.y, state.currentScreen.y);
  const width = Math.abs(state.currentScreen.x - state.startScreen.x);
  const height = Math.abs(state.currentScreen.y - state.startScreen.y);

  return (
    <div
      data-testid="zoom-rubber-rect"
      className="scene3d-zoom-rubber"
      style={{
        position: "absolute",
        left,
        top,
        width,
        height,
        pointerEvents: "none",
        zIndex: 7,
      }}
    />
  );
}

export interface ZoomRubberProps {
  /** First click in CSS pixels (relative to the canvas parent). */
  readonly firstCorner: { x: number; y: number } | null;
  /** Current mouse position in CSS pixels. */
  readonly currentCursor: { x: number; y: number } | null;
}

/**
 * Pure presentation. No R3F dependencies — mounts cleanly under any
 * HTML overlay container. Returns `null` when the rubber band should
 * not paint.
 */
export function ZoomRubber(
  props: ZoomRubberProps,
): React.ReactElement | null {
  const { firstCorner, currentCursor } = props;
  if (!firstCorner || !currentCursor) return null;

  const left = Math.min(firstCorner.x, currentCursor.x);
  const top = Math.min(firstCorner.y, currentCursor.y);
  const width = Math.abs(currentCursor.x - firstCorner.x);
  const height = Math.abs(currentCursor.y - firstCorner.y);

  // L-to-R drag → "window" mode (solid blue tint), R-to-L → "crossing"
  // mode (green tint). Mirrors AutoCAD parity + the SVG stage's
  // `drawing-stage-zoom-rubber` styling.
  const crossing = currentCursor.x < firstCorner.x;
  const className =
    "scene3d-zoom-rubber " +
    (crossing ? "is-crossing" : "is-window");

  return (
    <div
      data-testid="scene3d-zoom-rubber"
      className={className}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        transform: `translate(${left}px, ${top}px)`,
        width,
        height,
        border: crossing
          ? "1px dashed rgba(0, 200, 100, 0.85)"
          : "1px dashed rgba(70, 140, 220, 0.85)",
        background: crossing
          ? "rgba(0, 200, 100, 0.08)"
          : "rgba(70, 140, 220, 0.08)",
        pointerEvents: "none",
        zIndex: 7,
      }}
    />
  );
}
